import type { ReactNode } from "react";
import { cn } from "@nexus/ui";

export interface SettingsRowProps {
  title?: ReactNode;
  hint?: ReactNode;
  /** Extra content under the heading copy, e.g. a data-source note. */
  headingExtra?: ReactNode;
  children: ReactNode;
  className?: string;
  /** When false, omit the bottom border (last row in a tab). */
  bordered?: boolean;
  /** Render only the control column (continuation row under a prior heading). */
  hideHeading?: boolean;
  "data-testid"?: string;
}

/** Two-column settings layout: 220px label column + fluid controls (Wonder org drawer). */
export function SettingsRow({
  title,
  hint,
  headingExtra,
  children,
  className,
  bordered = true,
  hideHeading = false,
  "data-testid": dataTestId,
}: SettingsRowProps) {
  return (
    <div
      data-testid={dataTestId}
      className={cn(
        hideHeading
          ? "px-8 pb-6 sm:grid sm:grid-cols-[220px_1fr] sm:gap-8"
          : "grid grid-cols-1 gap-4 px-8 py-6 sm:grid-cols-[220px_1fr] sm:gap-8",
        bordered && "border-b border-border-subtle",
        className,
      )}
    >
      {hideHeading ? (
        <div className="hidden sm:block" aria-hidden />
      ) : (
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-0.5">
            <div className="text-[13px] font-semibold leading-snug text-foreground">{title}</div>
            {hint ? <p className="text-xs leading-normal text-muted-foreground">{hint}</p> : null}
          </div>
          {headingExtra}
        </div>
      )}
      <div className={cn("flex min-w-0 flex-col gap-4", hideHeading && "sm:col-start-2")}>{children}</div>
    </div>
  );
}

export interface SettingsFieldProps {
  label: ReactNode;
  htmlFor?: string;
  required?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  /** Id for the error message, so the control can point at it with `aria-describedby`. */
  errorId?: string;
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function SettingsField({
  label,
  htmlFor,
  required,
  hint,
  error,
  errorId,
  extra,
  children,
  className,
}: SettingsFieldProps) {
  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex items-center justify-between gap-2">
        <label htmlFor={htmlFor} className="flex gap-0.5 text-xs font-medium text-foreground">
          <span>{label}</span>
          {required ? <span className="text-destructive" aria-hidden>*</span> : null}
        </label>
        {extra}
      </div>
      {children}
      {error ? (
        <p id={errorId} className="text-[11px] text-destructive" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-[11px] leading-snug text-muted-foreground">{hint}</p>
      ) : null}
    </div>
  );
}
